import React, { type ReactNode, type Ref } from 'react';

import IconButton, { type IconButtonProps } from '@atlaskit/button/icon/button';
import type { AnyEntryPoint } from '@atlassian/entry-point-types/types';

import { ModalTrigger, type ModalTriggerProps as ModalTriggerProps } from './modal-trigger';

export type ModalIconButtonTriggerProps<TEntryPoint> = Omit<
	ModalTriggerProps<TEntryPoint>,
	'children' | 'title'
> & {
	/**
	 * Title shown in the modal while the entry point loads or fails.
	 */
	modalTitle?: ReactNode;
} & Omit<IconButtonProps, 'ref'>; // Prevent ref prop collision between ButtonProps and render prop

export function ModalIconButtonTrigger<TEntryPoint extends AnyEntryPoint>({
	entryPoint,
	entryPointParams,
	entryPointProps,
	Fallback,
	modalTitle,
	modalProps,
	onRequestClose,
	forwardedRef,
	preloadOnFocus,
	loadingText,
	source,
	...buttonProps
}: ModalIconButtonTriggerProps<TEntryPoint>): React.JSX.Element {
	return (
		<ModalTrigger
			forwardedRef={forwardedRef}
			entryPoint={entryPoint}
			entryPointParams={entryPointParams}
			entryPointProps={entryPointProps}
			Fallback={Fallback}
			title={modalTitle}
			modalProps={modalProps}
			onRequestClose={onRequestClose}
			preloadOnFocus={preloadOnFocus}
			loadingText={loadingText}
			source={source}
		>
			{({ ref, ...triggerProps }) => (
				<IconButton ref={ref as Ref<HTMLButtonElement>} {...buttonProps} {...triggerProps} />
			)}
		</ModalTrigger>
	);
}
